import { useState, useEffect, useCallback, useRef } from 'react';
import { IKeysetPaginationResult } from '../types/search';

interface UseKeysetDataResult<T> {
  items: T[];
  loading: boolean;
  error: boolean;
  hasNext: boolean;
  totalCount: number;
  refetch: () => void;
}

/**
 * Encapsulates keyset-paginated data fetching: loading/error/items/hasNext/totalCount state,
 * race-condition protection via cancelled flag, and an explicit refetch() trigger.
 *
 * @param fetcher - Called on every dep change; captures cursor/pageSize from its closure.
 * @param deps    - Re-fetch whenever any value changes (same semantics as useEffect deps).
 */
export function useKeysetData<T>(
  fetcher: () => Promise<IKeysetPaginationResult<T>>,
  deps: readonly unknown[]
): UseKeysetDataResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [refetchKey, setRefetchKey] = useState(0);

  // Always call the latest fetcher without including it in deps
  // (fetcher is a new closure each render but semantically stable).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refetch = useCallback(() => setRefetchKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetcherRef.current()
      .then((data) => {
        if (!cancelled) {
          setItems([...data.items]);
          setHasNext(data.hasNext);
          setTotalCount(data.totalCount);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, refetchKey]);

  return { items, loading, error, hasNext, totalCount, refetch };
}